import {API} from '../common/axios'

export type TransferOrderItem = {
    sku: string
    quantity: number
    serials: string[]
    model_number: string | null
    location?: string
}

export type TransferOrderReceivedItem = {
    sku: string
    quantity: number
    description: string
    receivedAt: Date,
    serials: string[]
}

export type Tracking = {
    number: string
    carrier: string
    estimatedDelivery: Date
    status: string,
    message: string
}

export type TransferOrderResult = {
    transferOrderId: number
    transferRequestId: number
    type: string
    status: string
    items: TransferOrderItem[]
    receivedItems: TransferOrderReceivedItem[]
    tracking: Tracking | null
    completed_at: string | null
    to_location_id: string
}

export type OpenReceiptResult = {
    transferOrderId: number
    type: string
    status: string
    items: TransferOrderItem[]
    tracking: Tracking | null
    completed_at: string | null
    to_location_id: string
}

export type ExpectedSerials = {
    serial: string
    quantity: number
}

export type ExpectedNonSerials = {
    sku: string
    model_number: string | null
    location: string | null
    quantity: number
    progress?: string
    enteredAt: string | null
}

export type SearchTransferResult = {
    transferRequestId: number,
    status: string,
    description: string,
    fromLocationId: string,
    toLocationId: string,
    contactName: string,
    email: string,
    city: string,
    state: string,
    whOrderId: number,
    sku: string,
    serialized: boolean,
    quantity: number,
    serials: string[] | null,
    approvedBy: string | null,
    approvedAt: Date | null,
    rejectedReason: string | null,
    requestedAt: Date,
    requestedBy: string,
    assignedTo: string,
    selectedAt: Date | null,
    selectedBy: string | null,
    completedAt: Date | null,
    completedBy: string | null,
    fromContactName: string | null,
}

export type TransferRequest = {
    transferRequestId: number;
    status: string;
    description: string;
    fromLocationId: string;
    toLocationId: string;
    contactName: string;
    email: string;
    city: string;
    state: string;
    inStock: number;
    inTransit: number;
    pendingPickup: number;
    pendingReceipt: number;
    whOrderId: number;
    sku: string;
    serialized: boolean;
    quantity: number;
    serials: string[] | null;
    rejectedReason: string | null;
    requestedAt: Date;
    requestedBy: string;
    assignedTo: string | null;
    selectedAt: Date | null;
    selectedBy: string | null;
    completedAt: Date | null;
    completedBy: string | null;
    fromCandidates: FromCandidate[];
}

export type FromCandidate = {
    locationId: string;
    contactName: string;
    email: string;
    city: string;
    state: string;
    inStock: number;
}

const toDate = (value: string | null) => (value && new Date(value)) || null

export const searchTransfers = async (params: any): Promise<TransferOrderResult[]> => {
    let response = await API.get('inventory/transfers', {params})
    return response.data.map((t: any): TransferOrderResult => ({
        transferOrderId: t.transfer_order_id,
        transferRequestId: t.transfer_request_id,
        type: t.type,
        status: t.status,
        items: t.items.map((x: any): TransferOrderItem => ({
            sku: x.sku,
            quantity: x.quantity,
            serials: x.serials,
            model_number: x.model_number
        })),
        receivedItems: t.received_items.map((x: any): TransferOrderReceivedItem => ({
            sku: x.sku,
            quantity: x.quantity,
            description: x.description,
            receivedAt: new Date(x.received_at),
            serials: x.serials
        })),
        tracking: t.tracking ? {
            number: t.tracking.number,
            carrier: t.tracking.carrier,
            estimatedDelivery: new Date(t.tracking.estimated_delivery),
            status: t.tracking.status,
            message: t.tracking.message
        } : null,
        completed_at: t.completed_at,
        to_location_id: t.to_location_id
    }))
}

export const openReceipt = async (transfer_order_id: number) => {
    let response = await API.post(`inventory/transfers/${transfer_order_id}/open-receipt`)
    const t = response.data
    let result: OpenReceiptResult = {
        transferOrderId: t.transfer_order_id,
        type: t.type,
        status: t.status,
        items: t.items.map((x: any): TransferOrderItem => ({
            sku: x.sku,
            quantity: x.quantity,
            serials: x.serials,
            model_number: x.model_number
        })),
        tracking: t.tracking ? {
            number: t.tracking.number,
            carrier: t.tracking.carrier,
            estimatedDelivery: new Date(t.tracking.estimated_delivery),
            status: t.tracking.status,
            message: t.tracking.message
        } : null,
        completed_at: t.completed_at,
        to_location_id: t.to_location_id
    }
    return result
}

export const submitReceipt = async (transfer_order_id: number) => {
    await API.post(`inventory/transfers/${transfer_order_id}/submit-receipt`)
}

export const searchTransferRequests = async (params: any) => {
    let response = await API.get('inventory/transfer-requests', {params})

    return response.data.map(
        (t: any): SearchTransferResult => ({
                transferRequestId: t.transfer_request_id,
                status: t.status,
                description: t.description,
                fromLocationId: t.from_location_id,
                toLocationId: t.to_location_id,
                contactName: t.contact_name,
                email: t.email,
                city: t.city,
                state: t.state,
                whOrderId: t.wh_order_id,
                sku: t.sku,
                serialized: t.serialized,
                quantity: t.quantity,
                serials: t.serials,
                approvedBy: t.approved_by,
                approvedAt: toDate(t.approved_at),
                rejectedReason: t.rejected_reason,
                requestedAt: new Date(t.requested_at),
                requestedBy: t.requested_by,
                assignedTo: t.assigned_to,
                selectedAt: toDate(t.selectedAt),
                selectedBy: t.selectedBy,
                completedAt: toDate(t.completedAt),
                completedBy: t.completedBy,
                fromContactName: t.from_contact_name
            }
        )
    )
}

export const checkInStockItems = async (params: any) => {
    let response = await API.get('inventory/locations/snapshot', {params})
    return response
}

export const createTransferRequest = async (
    location_id: string,
    sku: string,
    quantity: number,
    description: string,
) => {
    let resp = await API.post(`inventory/transfer-requests`, {location_id, sku, quantity, description})
}

export const getTransferRequest = async (transfer_request_id: number) => {
    let response = await API.get(`inventory/transfer-requests/${transfer_request_id}`)
    let t = response.data
    return {
        transferRequestId: t.transfer_request_id,
        status: t.status,
        description: t.description,
        fromLocationId: t.from_location_id,
        toLocationId: t.to_location_id,
        contactName: t.contact_name,
        email: t.email,
        city: t.city,
        state: t.state,
        inStock: t.in_stock,
        inTransit: t.in_transit,
        pendingPickup: t.pending_pickup,
        pendingReceipt: t.pending_receipt,
        whOrderId: t.wh_order_id,
        sku: t.sku,
        serialized: t.serialized,
        quantity: t.quantity,
        serials: t.serials,
        rejectedReason: t.rejected_reason,
        requestedAt: new Date(t.requested_at),
        requestedBy: t.requested_by,
        assignedTo: t.assigned_to,
        selectedAt: toDate(t.selected_at),
        selectedBy: t.selected_by,
        completedAt: toDate(t.completed_at),
        completedBy: t.completed_by,
        fromCandidates: t.from_candidates.map(
            (candidate: any): FromCandidate => ({
                locationId: candidate.location_id,
                contactName: candidate.contact_name,
                email: candidate.email,
                city: candidate.city,
                state: candidate.state,
                inStock: candidate.in_stock

            })
        )
    }
}

export const approveTransferRequest = async (
    transfer_request_id: number,
    from_location_id: string,
    carrier: string
) => {
    let resp = await API.post(`inventory/transfer-requests/${transfer_request_id}/approve`,
        {from_location_id, carrier}
    )
}

export const rejectTransferRequest = async (
    transfer_request_id: number,
    rejected_reason: string
) => {
    let resp = await API.post(`inventory/transfer-requests/${transfer_request_id}/reject`, {rejected_reason})
}

export const selectTransferRequestSerials = async (
    transfer_request_id: number,
    serials: string[]
) => {
    let resp = await API.post(`inventory/transfer-requests/${transfer_request_id}/select-serials`, {serials})
}
